import { useId } from 'react';
import type { DocumentV3 } from '@docgen/schema';
import { CardHeader, DatePicker, Field, Panel, TextArea, TextInput } from '@bcecloud/portal-ui';
import type { PricingResult, ProposalPricing, PricingTotals } from '../lib/api';
import { applyQuickEdit, readQuickEdits, type QuickEditField } from '../lib/quick-edits';
import { PricingPanel } from '../editor/PricingPanel';

/**
 * The pricing panel was drawn for a mouse beside the canvas (28px controls,
 * 12px type). On a phone every control inside it grows to a 40px finger
 * target, and text fields get 16px type so iOS does not zoom on focus.
 */
const TOUCH_SIZED =
  '[&_input:not([type=checkbox])]:min-h-10 [&_input:not([type=checkbox])]:text-[16px] [&_select]:min-h-10 [&_select]:text-[16px] [&_button]:min-h-10 [&_button]:min-w-10 [&_input[type=checkbox]]:size-5';

export interface QuickEditsProps {
  readonly proposalId: string;
  readonly doc: DocumentV3;
  readonly pricing: ProposalPricing;
  readonly totals: PricingTotals;
  /** A document edit, with the undo-coalescing key the editor's history uses. */
  readonly onChange: (next: DocumentV3, coalesceKey: string) => void;
  /** A pricing change's result: the server's re-projected proposal. */
  readonly onPricingResult: (result: PricingResult) => void;
}

/**
 * The edits a rep makes away from a desk, as plain stacked forms: the
 * introduction note, the event facts, the client contact, and the pricing.
 * Deal facts are ordinary document edits (they autosave through the same
 * path as the canvas); pricing is the desktop Pricing panel itself, so both
 * screens run the same API calls.
 */
export function QuickEdits({ proposalId, doc, pricing, totals, onChange, onPricingResult }: QuickEditsProps): React.ReactElement {
  const ids = useId();
  const values = readQuickEdits(doc);
  const set = (field: QuickEditField) => (value: string) => onChange(applyQuickEdit(doc, field, value), `quick:${field}`);
  const id = (field: QuickEditField): string => `${ids}-${field}`;

  return (
    <div className="space-y-4 p-4">
      <Panel className="p-4">
        <CardHeader
          title="Introduction"
          description="The opening letter on the first page after the cover."
        />
        <Field
          label="Your note"
          htmlFor={id('introNote')}
          hint="Leave it empty to use the standard introduction written from the deal."
        >
          <TextArea
            id={id('introNote')}
            rows={6}
            className="w-full"
            value={values.introNote}
            onChange={(event) => set('introNote')(event.target.value)}
          />
        </Field>
      </Panel>

      <Panel className="p-4">
        <CardHeader title="Event" description="Where and when, as the cover and introduction print it." />
        <div className="space-y-4">
          <Field label="Event date" htmlFor={id('eventDate')} hint="Shown on the cover and in the introduction.">
            <DatePicker id={id('eventDate')} value={values.eventDate || undefined} onChange={set('eventDate')} />
          </Field>
          <Field label="City" htmlFor={id('eventCity')} hint="Shown on the cover and in the introduction, for example Boston, MA.">
            <TextInput
              id={id('eventCity')}
              value={values.eventCity}
              autoComplete="off"
              onChange={(event) => set('eventCity')(event.target.value)}
            />
          </Field>
        </div>
      </Panel>

      <Panel className="p-4">
        <CardHeader title="Client contact" description="The person this proposal is addressed to." />
        <div className="space-y-4">
          <Field label="First name" htmlFor={id('contactFirstName')} hint="Used in the introduction's greeting, as in Hi Karen.">
            <TextInput
              id={id('contactFirstName')}
              value={values.contactFirstName}
              autoComplete="off"
              onChange={(event) => set('contactFirstName')(event.target.value)}
            />
          </Field>
        </div>
      </Panel>

      <Panel>
        <div className="px-4 pt-4">
          <CardHeader
            className="mb-0"
            title="Pricing"
            description="Options, programs, participants, prices, discounts and how long the pricing holds."
          />
        </div>
        <div className={TOUCH_SIZED}>
          <PricingPanel proposalId={proposalId} pricing={pricing} totals={totals} onResult={onPricingResult} />
        </div>
      </Panel>
    </div>
  );
}
